import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Users, Plus, Upload, Trash2, Download, FileDown, Pencil, Eye, EyeOff, Info, MapPin, Stethoscope, Ban, RotateCcw, Wifi, WifiOff, PowerOff, Wallet, Loader2, CheckCircle2, XCircle, Contact, Router, Gift } from 'lucide-react'
import api from '../api'
import { isAdmin } from '../auth'
import { Card, PageHeader, Badge, EmptyState, Field, Button, Alert, Modal, ModalHeader, Input, Textarea, Select, Pagination } from '../components/ui'
import LokasiMap from '../components/LokasiMap'
import DiagnostikModal from '../components/DiagnostikModal'

// Parser CSV sederhana — kolom: username,no_hp,nama. Baris pertama dianggap header kalau kolom
// pertamanya persis "username" (case-insensitive), supaya file yg dibikin dari Excel/Sheets langsung
// jalan tanpa perlu diedit dulu.
function parseCsv(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (lines.length && /^username\s*,/i.test(lines[0])) lines.shift()
  return lines.map(line => {
    const [ppp_username, no_hp, nama] = line.split(',').map(s => (s || '').trim())
    return { ppp_username, no_hp, nama }
  })
}

// Tombol aksi baris pelanggan -- icon + label teks pendek di bawahnya (2026-08-05, dulu icon-only
// & cuma mengandalkan `title` hover utk tahu fungsinya, sulit dikenali kalau belum terbiasa & sama
// sekali tidak kepakai di HP krn tidak ada hover di layar sentuh). Layout vertikal (icon di atas,
// label di bawah) supaya tetap ringkas horizontal walau ada 7-8 aksi berjejer, sekaligus tap target
// jadi lebih besar drpd icon polos 15px. `title` tetap dipertahankan sbg penjelasan lebih detail utk
// desktop hover (mis. Diagnostik menjelaskan apa saja yg dicek).
function AksiBtn({ onClick, title, color, disabled, icon: Icon, children }) {
  return (
    <button onClick={onClick} title={title} disabled={disabled} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
      color, opacity: disabled ? 0.5 : 1, padding: '2px 3px', minWidth: 44
    }}>
      <Icon size={15} />
      <span style={{ fontSize: 10, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{children}</span>
    </button>
  )
}

// Bungkus field dgn tanda kutip kalau mengandung koma/kutip/baris baru — supaya nama pelanggan
// yg kebetulan ada koma di dalamnya (mis. "Budi, Toko Jaya") tidak merusak kolom CSV.
function csvField(value) {
  const s = String(value ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function downloadCsv(filename, rows) {
  const csv = rows.map(row => row.map(csvField).join(',')).join('\r\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const STATUS_TONE = { AKTIF: 'success', ISOLIR: 'warning', BERHENTI: 'danger' }

// Lebar kolom sticky Username+Nama (2026-07-27) -- HARUS fixed (bukan auto) supaya offset `left`
// kolom Nama bisa dihitung pasti (persis selebar kolom Username), disini juga dipakai bareng oleh
// th & td spy header & body-nya presisi bertumpuk saat discroll.
const STICKY_COL1_W = 118
const STICKY_COL2_W = 140
const thStickyStyle = {
  padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)',
  position: 'sticky', zIndex: 2, background: '#fafafc'
}
const tdStickyStyle = {
  padding: '10px 16px', position: 'sticky', zIndex: 1, background: 'var(--surface)'
}

// Tanggal lokal browser admin, format YYYY-MM-DD -- SENGAJA bukan toISOString().slice(0,10) krn itu
// dikonversi ke UTC dulu (jam dini hari WIB bisa kepotong mundur satu hari). Dipakai sbg default
// Tanggal Aktif saat buka form Tambah, jg basis hitung Prorata (2026-07-22).
function hariIniLokal() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Prorata Prabayar (2026-07-22) -- preview di form, HARUS identik dgn hitungan server
// (buatTagihanProrataAwal di routes/pelangganKontak.js) spy admin tidak kaget angkanya beda pas submit.
function hitungProrata(hargaBulanan, tanggalAktif) {
  const tgl = tanggalAktif || hariIniLokal()
  const [tahun, bulan, hari] = tgl.split('-').map(Number)
  if (!tahun || !bulan || !hari) return null
  const hariDalamBulan = new Date(tahun, bulan, 0).getDate()
  const hariAktif = Math.min(Math.max(hari, 1), hariDalamBulan)
  const sisaHari = hariDalamBulan - hariAktif + 1
  const jumlah = Math.round(hargaBulanan * sisaHari / hariDalamBulan)
  return { jumlah, sisaHari, hariDalamBulan, tglAktif: tgl }
}

// NIK disamarkan spt password (2026-07-22) -- sisakan 4 digit depan+belakang biar admin masih bisa
// cocokkan sekilas tanpa nampilin nomor KTP penuh di layar.
function maskNik(nik) {
  const s = String(nik || '')
  if (s.length <= 8) return '*'.repeat(s.length)
  return s.slice(0, 4) + '*'.repeat(s.length - 8) + s.slice(-4)
}

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }

// Badge kecil di bawah nama paket -- ⭐ utk diskon selamanya (pelanggan spesial), 🎁 utk sekali pakai.
function BonusBadge({ list }) {
  if (!list || list.length === 0) return null
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
      {list.map(b => (
        <Badge key={b.id} tone="brand" style={{ fontSize: 10.5, padding: '2px 8px' }}>
          {b.masa === 'SELAMANYA' ? '⭐' : '🎁'} {b.label.replace(/^Diskon /, '').replace(/ - .*/, '')}
        </Badge>
      ))}
    </div>
  )
}

export default function PelangganKontak() {
  // Daftar Pelanggan (dulu "Pelanggan Kontak") -- Edit/Isolir/Kick/Hapus dibuka admin+teknisi
  // (2026-07-29, backend jg sudah dibuka, lihat requireAdmin yg dicopot di
  // server/routes/pelangganKontak.js). Format Import/Download Daftar/Import CSV SENGAJA dibalikkan
  // admin-only (2026-07-29 revisi) -- POST /import backend jg requireAdmin lagi.
  const admin = isAdmin()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [metodeFilter, setMetodeFilter] = useState('all')
  const [paketFilter, setPaketFilter] = useState('all')
  const [paketList, setPaketList] = useState([])

  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ ppp_username: '', no_hp: '', nama: '' })
  // null = mode Tambah (username baru, field bisa diisi bebas). Diisi username asli = mode Edit
  // (username jadi read-only -- backend upsert lewat PRIMARY KEY ppp_username, kalau usernamenya
  // diubah saat edit maka POST akan bikin baris BARU bukan me-rename baris lama, jadi baris lama
  // ketinggalan yatim. Field dikunci di UI supaya itu tidak bisa terjadi.)
  const [editingUsername, setEditingUsername] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [lokasiLoading, setLokasiLoading] = useState(false)
  const [lokasiError, setLokasiError] = useState('')
  const lokasiMapRef = useRef(null)
  const [isolirLoading, setIsolirLoading] = useState(null) // ppp_username yg lagi diproses toggle Isolir
  const [onlineSet, setOnlineSet] = useState(null) // null = belum dimuat/gagal (jgn tampilkan Offline keliru); Set = daftar username online
  const [ipMap, setIpMap] = useState({}) // username -> IP sesi PPPoE aktif, dipakai tombol Remote
  const [kickingUsername, setKickingUsername] = useState(null)

  const [importResult, setImportResult] = useState(null)
  const [importing, setImporting] = useState(false)
  const fileRef = useRef(null)
  const [daftarInfo, setDaftarInfo] = useState('')

  // Modal Detail (read-only, kedua role) -- fetch terpisah lewat GET /:username krn field sensitif
  // (password PPPoE) SENGAJA tidak ikut di respons list GET / (lihat komentar server-side).
  const [detailModal, setDetailModal] = useState(false)
  const [detailData, setDetailData] = useState(null)
  // Cross-link ke Perangkat Pelanggan (2026-07-24) -- reuse modal Diagnostik yg sama (sinyal ONT/
  // OLT, status PPPoE) tanpa perlu loncat menu, lihat komentar di components/DiagnostikModal.jsx.
  const [diagUsername, setDiagUsername] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showNik, setShowNik] = useState(false)
  const [saldoDompet, setSaldoDompet] = useState(null)
  const [linkTagihan, setLinkTagihan] = useState('')

  // Deposit instan (2026-07-28) -- tombol di baris Daftar Pelanggan & Detail, cuma minta nominal
  // (tanggal auto hari ini, backend POST /pembayaran/dompet/:username/topup yg isi default-nya).
  // Dibuka juga utk teknisi (dulu admin-only) krn teknisi di lapangan sering terima titipan deposit.
  // Gantikan tombol "Terima Pembayaran" bebas-pelanggan yg dulu ada di History Pembayaran (dihapus,
  // lihat komentar di HistoryPembayaran.jsx) utk kasus bayar-duluan sebelum tagihan digenerate.
  const [depositUser, setDepositUser] = useState(null) // { ppp_username, nama } atau null = modal tertutup
  const [depositJumlah, setDepositJumlah] = useState('')
  const [depositError, setDepositError] = useState('')
  // Toast (2026-07-28) -- form Deposit langsung TUTUP begitu Submit diklik (feedback user: dulu
  // form tetap kebuka nunggu ditutup manual, kerasa lambat), proses simpan lanjut di background
  // ditandai toast kecil kanan-bawah: loading -> centang/silang. `id` dipakai supaya setTimeout
  // pembersih toast lama tidak menghapus toast BARU kalau admin sempat deposit lagi sebelum toast
  // pertama hilang (lihat showToast()).
  const [toast, setToast] = useState(null) // { id, status: 'loading'|'success'|'error', text }

  // Debounce: tunggu user berhenti mengetik dulu sebelum query ke server -- mirror pola Perangkat.jsx
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  // ?search=<username> di URL (2026-08-29) -- dipakai link "Buka di Daftar Pelanggan" dari laporan
  // Auth Gagal (PengaturanMikrotik.jsx), supaya langsung kefilter tanpa perlu ketik ulang. Cuma
  // dibaca sekali saat halaman ini dibuka (array dependency kosong), bukan disinkronkan balik ke URL.
  useEffect(() => {
    const q = searchParams.get('search')
    if (q) setSearchInput(q)
  }, [searchParams])

  useEffect(() => { load(); loadOnlineStatus() }, [page, limit, search, statusFilter, metodeFilter, paketFilter])
  // Badge bonus/diskon aktif (2026-09-29) -- 1 request utk semua pelanggan, bukan per baris.
  const [bonusMap, setBonusMap] = useState({})
  useEffect(() => { api.get('/bonus-pelanggan/aktif-ringkas').then(r => setBonusMap(r.data)).catch(() => {}) }, [])
  useEffect(() => { loadPaket() }, [])

  // Pagination+filter (2026-07-22) -- 351+ baris sudah kebanyakan utk di-load sekaligus & difilter
  // client-side spt sebelumnya, jadi ini query server-side (mirror Perangkat.jsx).
  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/pelanggan-kontak', { params: {
        page, limit, search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        metode_bayar: metodeFilter !== 'all' ? metodeFilter : undefined,
        paket_langganan_id: paketFilter !== 'all' ? paketFilter : undefined
      } })
      setData(res.data.items)
      setTotal(res.data.total)
    } finally { setLoading(false) }
  }

  // Pakai endpoint publik (bukan /paket-langganan yg 100% admin-only, lihat komentar server
  // routes/paketLangganan.js) supaya teknisi jg bisa lihat daftar paket saat Daftar/Edit pelanggan
  // (2026-07-22, perluasan akses PSB) -- sudah difilter aktif=1 & cuma nama+harga di server.
  async function loadPaket() {
    const res = await api.get('/public/paket-langganan')
    setPaketList(res.data)
  }

  // Status online Mikrotik SATU panggilan utk seluruh halaman (lihat komentar server), refresh tiap
  // ganti halaman/filter spy tetap kebaca terkini. Gagal (mis. belum ada Mikrotik aktif) -> biarkan
  // onlineSet null, kolom tampil "-" alih-alih "Offline" yg keliru.
  async function loadOnlineStatus() {
    try {
      const res = await api.get('/pelanggan-kontak/mikrotik/online')
      setOnlineSet(new Set(res.data.online))
      setIpMap(res.data.ip || {})
    } catch { setOnlineSet(null) }
  }

  function bukaRemote(username) {
    const ip = ipMap[username]
    if (!ip) return
    window.open(`http://${ip}`, '_blank', 'noopener,noreferrer')
  }

  async function kick(username) {
    if (!window.confirm(`Putus sesi PPPoE "${username}" sekarang? Koneksi akan terputus sebentar lalu CPE biasanya connect ulang otomatis (dipakai supaya paket baru langsung kepakai).`)) return
    setKickingUsername(username)
    try {
      await api.post(`/pelanggan-kontak/${username}/kick`)
      await loadOnlineStatus()
    } catch (e) {
      alert(e.response?.data?.message || 'Gagal kick sesi')
    } finally { setKickingUsername(null) }
  }

  async function bukaDetail(username) {
    setDetailModal(true)
    setDetailLoading(true)
    setShowPassword(false)
    setShowNik(false)
    try {
      const [detailRes, dompetRes, linkRes] = await Promise.all([
        api.get(`/pelanggan-kontak/${encodeURIComponent(username)}`),
        api.get(`/pembayaran/dompet/${encodeURIComponent(username)}`),
        api.get(`/pelanggan-kontak/${encodeURIComponent(username)}/link-tagihan`)
      ])
      setDetailData(detailRes.data)
      setSaldoDompet(dompetRes.data.saldo)
      setLinkTagihan(linkRes.data.url)
    } finally { setDetailLoading(false) }
  }

  // Dipakai bareng oleh tabel (desktop) & kartu (HP) -- lihat komentar .list-cards di index.css.
  function aksiPelanggan(d, online) {
    return (
      <>
        <AksiBtn onClick={() => bukaDetail(d.ppp_username)} title="Detail" color="var(--text-muted)" icon={Info}>Detail</AksiBtn>
        <AksiBtn onClick={() => navigate(`/pelanggan-kontak/${encodeURIComponent(d.ppp_username)}/client-area`)}
          title="Client Area -- data diri, ringkasan tagihan & history pembayaran dalam 1 halaman" color="var(--brand)" icon={Contact}>Client Area</AksiBtn>
        <AksiBtn onClick={() => setDiagUsername(d.ppp_username)} title="Diagnostik (sinyal ONT, status PPPoE, sinyal OLT)" color="var(--text-muted)" icon={Stethoscope}>Diagnostik</AksiBtn>
        {online && ipMap[d.ppp_username] && (
          <AksiBtn onClick={() => bukaRemote(d.ppp_username)}
            title={`Remote -- buka ${ipMap[d.ppp_username]} di tab baru (akses web admin modem/router pelanggan)`}
            color="var(--brand)" icon={Router}>Remote</AksiBtn>
        )}
        {admin && (
          <AksiBtn onClick={() => navigate(`/bonus-pelanggan?ppp_username=${encodeURIComponent(d.ppp_username)}&beri=${encodeURIComponent(d.ppp_username)}`)}
            title="Beri bonus/diskon tagihan untuk pelanggan ini" color="var(--brand)" icon={Gift}>Bonus</AksiBtn>
        )}
        <AksiBtn onClick={() => bukaDeposit(d)} title="Deposit -- catat pembayaran/titipan di muka (masuk saldo dompet, otomatis kepakai pas tagihan berikutnya)" color="var(--success)" icon={Wallet}>Deposit</AksiBtn>
        <AksiBtn onClick={() => bukaEditLengkap(d.ppp_username)} title="Edit" color="var(--text-muted)" icon={Pencil}>Edit</AksiBtn>
        <AksiBtn onClick={() => toggleIsolir(d)} disabled={isolirLoading === d.ppp_username}
          title={d.status === 'ISOLIR' ? 'Aktifkan Kembali' : 'Isolir (tanda status saja)'}
          color={d.status === 'ISOLIR' ? 'var(--success)' : 'var(--warning)'}
          icon={d.status === 'ISOLIR' ? RotateCcw : Ban}>{d.status === 'ISOLIR' ? 'Aktifkan' : 'Isolir'}</AksiBtn>
        {online && (
          <AksiBtn onClick={() => kick(d.ppp_username)} disabled={kickingUsername === d.ppp_username}
            title="Kick -- putus sesi PPPoE sebentar (mis. supaya paket baru langsung kepakai)" color="var(--warning)" icon={PowerOff}>Kick</AksiBtn>
        )}
        <AksiBtn onClick={() => hapus(d.ppp_username)} title="Hapus" color="var(--danger)" icon={Trash2}>Hapus</AksiBtn>
      </>
    )
  }

  function bukaDeposit(d) {
    setDepositUser({ ppp_username: d.ppp_username, nama: d.nama })
    setDepositJumlah(''); setDepositError('')
  }

  function showToast(status, text) {
    const id = Date.now()
    setToast({ id, status, text })
    if (status !== 'loading') {
      setTimeout(() => setToast(t => (t && t.id === id) ? null : t), status === 'error' ? 6000 : 4000)
    }
    return id
  }

  // Tutup form SEGERA setelah validasi lolos (tidak nunggu respons server) -- proses simpan lanjut
  // di background, statusnya dilaporkan lewat toast (loading -> sukses/gagal). WA konfirmasi ke
  // pelanggan dikirim otomatis oleh backend (kirimDepositWa di routes/pembayaran.js), tidak perlu
  // aksi tambahan di sini.
  async function simpanDeposit() {
    const jumlah = Number(depositJumlah)
    if (!jumlah || jumlah <= 0) return setDepositError('Jumlah harus lebih dari 0')
    const target = depositUser
    setDepositUser(null)
    const id = showToast('loading', `Menyimpan deposit ${rp(jumlah)} untuk ${target.nama || target.ppp_username}...`)
    try {
      const res = await api.post(`/pembayaran/dompet/${encodeURIComponent(target.ppp_username)}/topup`, { jumlah })
      setToast({ id, status: 'success', text: `Deposit tersimpan. Saldo dompet sekarang ${rp(res.data.saldo)}.` })
      setTimeout(() => setToast(t => (t && t.id === id) ? null : t), 4500)
      // Kalau modal Detail lagi kebuka utk pelanggan yg sama, saldo di sana ikut ke-update.
      if (detailModal && detailData?.ppp_username === target.ppp_username) setSaldoDompet(res.data.saldo)
    } catch (e) {
      setToast({ id, status: 'error', text: e.response?.data?.message || 'Gagal menyimpan deposit' })
      setTimeout(() => setToast(t => (t && t.id === id) ? null : t), 6000)
    }
  }

  function bukaTambah() {
    // Form penuh utk SEMUA role (2026-07-22, perluasan akses PSB) -- teknisi bisa Daftar pelanggan
    // lengkap sendiri (paket/NIK/lokasi/dst) tanpa perlu admin lengkapi belakangan.
    setForm({
      ppp_username: '', no_hp: '', nama: '', ppp_password: '', alamat: '', paket_langganan_id: '',
      nik: '', npwp: '', latitude: '', longitude: '', ip_address: '', tanggal_aktif: hariIniLokal(),
      metode_bayar: 'PASCABAYAR', tanggal_jatuh_tempo: 11, status: 'AKTIF'
    })
    setEditingUsername(null)
    setError('')
    setLokasiError('')
    setDaftarInfo('')
    setModal(true)
  }

  function bukaEdit(row) {
    setForm({
      ppp_username: row.ppp_username, no_hp: row.no_hp, nama: row.nama || '',
      alamat: row.alamat || '', paket_langganan_id: row.paket_langganan_id || '',
      ppp_password: row.ppp_password || '', ip_address: row.ip_address || '',
      nik: row.nik || '', npwp: row.npwp || '', latitude: row.latitude || '', longitude: row.longitude || '',
      tanggal_aktif: row.tanggal_aktif || '', metode_bayar: row.metode_bayar || 'PASCABAYAR',
      tanggal_jatuh_tempo: row.tanggal_jatuh_tempo || 1, status: row.status || 'AKTIF'
    })
    setEditingUsername(row.ppp_username)
    setError('')
    setLokasiError('')
    setDaftarInfo('')
    setModal(true)
  }

  // Edit (admin) butuh data LENGKAP (termasuk password) yg tidak ada di baris tabel (list sengaja
  // tidak bawa password) -- fetch detail dulu baru buka modal edit.
  async function bukaEditLengkap(username) {
    const res = await api.get(`/pelanggan-kontak/${encodeURIComponent(username)}`)
    bukaEdit(res.data)
  }

  // Ambil Lokasi (2026-07-22) -- pakai Geolocation API device yg lagi buka form ini, jadi HARUS
  // diklik pas admin/teknisi benar2 di lokasi pelanggan (bukan bisa diisi dari kantor). Field tetap
  // bisa diedit manual kalau GPS meleset atau mau koreksi belakangan.
  function ambilLokasi() {
    if (!navigator.geolocation) { setLokasiError('Browser ini tidak mendukung Geolocation'); return }
    setLokasiLoading(true)
    setLokasiError('')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6)
        const lng = pos.coords.longitude.toFixed(6)
        setForm(f => ({ ...f, latitude: lat, longitude: lng }))
        lokasiMapRef.current?.setPosition(lat, lng)
        setLokasiLoading(false)
      },
      (err) => {
        setLokasiError('Gagal ambil lokasi: ' + err.message)
        setLokasiLoading(false)
      },
      { enableHighAccuracy: true, timeout: 15000 }
    )
  }

  async function simpan() {
    setError('')
    if (!form.ppp_username.trim()) return setError('Username PPP wajib diisi')
    if (!form.no_hp.trim()) return setError('Nomor HP wajib diisi')
    if (!editingUsername && !String(form.nik || '').trim()) {
      return setError('NIK wajib diisi saat mendaftarkan pelanggan baru')
    }
    if (!editingUsername && form.metode_bayar === 'PRABAYAR' && !form.paket_langganan_id) {
      return setError('Paket Langganan wajib dipilih utk pelanggan Prabayar (dipakai hitung tagihan prorata)')
    }
    setSaving(true)
    try {
      const res = await api.post('/pelanggan-kontak', form)
      setModal(false)
      if (res.data.tagihan_prorata) {
        const tp = res.data.tagihan_prorata
        setDaftarInfo(`Pelanggan terdaftar & tagihan prorata dibuat: Rp ${Number(tp.jumlah).toLocaleString('id-ID')} (${tp.sisa_hari} hari, periode ${tp.periode}).`)
      } else {
        setDaftarInfo('')
      }
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  async function hapus(username) {
    if (!window.confirm(`Hapus kontak "${username}"?`)) return
    await api.delete(`/pelanggan-kontak/${encodeURIComponent(username)}`)
    load()
  }

  // Toggle Isolir manual (2026-07-26, Fase 2) -- SENGAJA murni ubah `status` di DB lewat endpoint
  // upsert yg sudah ada (POST /pelanggan-kontak). `no_hp` WAJIB ikut dikirim (beda dari field
  // billing lain yg opsional lewat KOLOM_BILLING_OPSIONAL) -- upsertPelangganKontak() nolak 400
  // "Nomor HP tidak valid" kalau ini kosong, ditemukan lewat verifikasi live sebelum rilis. Field
  // lain (paket, alamat, dst) tetap tidak dikirim & dipertahankan apa adanya. TIDAK menyentuh
  // Mikrotik/RADIUS sama sekali (integrasi jaringan/walled-garden ditunda sampai migrasi RADIUS
  // selesai -- lihat catatan di server/pelangganKontakShared.js), makanya konfirmasinya eksplisit
  // bilang ini cuma tanda di sistem.
  async function toggleIsolir(d) {
    const isolirSekarang = d.status === 'ISOLIR'
    const statusBaru = isolirSekarang ? 'AKTIF' : 'ISOLIR'
    const pesan = isolirSekarang
      ? `Aktifkan kembali "${d.nama || d.ppp_username}"?`
      : `Isolir "${d.nama || d.ppp_username}"? Ini CUMA tanda status di sistem -- BELUM memutus internet pelanggan (integrasi jaringan menyusul).`
    if (!window.confirm(pesan)) return
    setIsolirLoading(d.ppp_username)
    try {
      await api.post('/pelanggan-kontak', { ppp_username: d.ppp_username, no_hp: d.no_hp, nama: d.nama, status: statusBaru })
      load()
    } finally { setIsolirLoading(null) }
  }

  function downloadFormatImport() {
    downloadCsv('format-import-pelanggan-kontak.csv', [
      ['username', 'no_hp', 'nama'],
      ['budi01', '08123456789', 'Budi Santoso']
    ])
  }

  // Export SENGAJA fetch terpisah dgn limit besar (bukan pakai `data`) -- `data` sekarang cuma isi
  // 1 halaman krn pagination server-side (2026-07-22). Filter aktif tetap ikut (search/status/dst),
  // page/limit di-skip spy semua baris yg cocok filter ikut ke-export.
  async function downloadDaftarKontak() {
    const res = await api.get('/pelanggan-kontak', { params: {
      limit: 100000, search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      metode_bayar: metodeFilter !== 'all' ? metodeFilter : undefined,
      paket_langganan_id: paketFilter !== 'all' ? paketFilter : undefined
    } })
    downloadCsv('daftar-pelanggan-kontak.csv', [
      ['username', 'no_hp', 'nama'],
      ...res.data.items.map(d => [d.ppp_username, d.no_hp, d.nama || ''])
    ])
  }

  async function pilihFileCsv(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportResult(null)
    setImporting(true)
    try {
      const text = await file.text()
      const rows = parseCsv(text)
      const res = await api.post('/pelanggan-kontak/import', { rows })
      setImportResult(res.data)
      load()
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <PageHeader
          title="Daftar Pelanggan"
          subtitle={`${total} pelanggan terdaftar — dipakai chatbot AI utk mengenali pelanggan yang chat`}
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {admin && <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={pilihFileCsv} />}
          {admin && <Button variant="soft" icon={FileDown} onClick={downloadFormatImport}>Format Import</Button>}
          {admin && <Button variant="soft" icon={Download} onClick={downloadDaftarKontak} disabled={total === 0}>Download Daftar</Button>}
          {admin && <Button variant="soft" icon={Upload} loading={importing} onClick={() => fileRef.current?.click()}>Import CSV</Button>}
          <Button icon={Plus} onClick={bukaTambah}>Daftar</Button>
        </div>
      </div>

      {daftarInfo && <Alert type="success">{daftarInfo}</Alert>}

      {importResult && (
        <Alert type={importResult.gagal.length ? 'warning' : 'success'}>
          Import selesai: {importResult.sukses} berhasil, {importResult.gagal.length} gagal.
          {importResult.gagal.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              {importResult.gagal.slice(0, 10).map((g, i) => <div key={i}>Baris {g.baris}: {g.alasan}</div>)}
              {importResult.gagal.length > 10 && <div>...dan {importResult.gagal.length - 10} lainnya</div>}
            </div>
          )}
        </Alert>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Input value={searchInput} onChange={e => setSearchInput(e.target.value)}
          placeholder="Cari username / nomor HP / nama..." style={{ flex: '1 1 220px', minWidth: 220 }} />
        <Select value={statusFilter} onChange={e => { setPage(1); setStatusFilter(e.target.value) }} style={{ width: 150 }}>
          <option value="all">Semua Status</option>
          <option value="AKTIF">Aktif</option>
          <option value="ISOLIR">Isolir</option>
          <option value="BERHENTI">Berhenti</option>
        </Select>
        <Select value={metodeFilter} onChange={e => { setPage(1); setMetodeFilter(e.target.value) }} style={{ width: 160 }}>
          <option value="all">Semua Metode Bayar</option>
          <option value="PASCABAYAR">Pascabayar</option>
          <option value="PRABAYAR">Prabayar</option>
        </Select>
        <Select value={paketFilter} onChange={e => { setPage(1); setPaketFilter(e.target.value) }} style={{ width: 170 }}>
          <option value="all">Semua Paket</option>
          {paketList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
        </Select>
      </div>

      <Card style={{ overflow: 'hidden' }}>
        {/* Desktop: tabel sticky Username+Nama. HP (<=860px): kartu -- lihat komentar .list-cards
            di index.css (sticky 2 kolom di layar sempit makan ~66-77% lebar, sisa scroll < 1 kolom).
            Keduanya SELALU di-render, CSS yg pilih mana tampil (mirror pola sidebar/drawer). */}
        <div className="list-table">
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafc' }}>
              <th style={{ ...thStickyStyle, left: 0, minWidth: STICKY_COL1_W }}>Username PPP</th>
              <th style={{ ...thStickyStyle, left: STICKY_COL1_W, minWidth: STICKY_COL2_W, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>Nama</th>
              {['Paket', 'Status', 'Mikrotik', 'Nomor HP', ''].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><EmptyState icon={Users} title="Memuat..." /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={7}><EmptyState icon={Users} title="Belum ada kontak pelanggan" /></td></tr>
            ) : data.map(d => {
              const online = onlineSet ? onlineSet.has(d.ppp_username) : null
              return (
              <tr key={d.ppp_username} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...tdStickyStyle, left: 0, minWidth: STICKY_COL1_W, fontWeight: 600 }}>{d.ppp_username}</td>
                <td style={{ ...tdStickyStyle, left: STICKY_COL1_W, minWidth: STICKY_COL2_W, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>{d.nama || '-'}</td>
                <td style={{ padding: '10px 16px' }}>{d.nama_paket || '-'}<BonusBadge list={bonusMap[d.ppp_username]} /></td>
                <td style={{ padding: '10px 16px' }}><Badge tone={STATUS_TONE[d.status] || 'neutral'}>{d.status || 'AKTIF'}</Badge></td>
                <td style={{ padding: '10px 16px' }}>
                  {online === null ? <span style={{ color: 'var(--text-faint)' }}>-</span> :
                    <Badge tone={online ? 'success' : 'neutral'}>
                      {online ? <Wifi size={11} /> : <WifiOff size={11} />} {online ? 'Online' : 'Offline'}
                    </Badge>}
                </td>
                <td style={{ padding: '10px 16px' }}><Badge tone="neutral">{d.no_hp}</Badge></td>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>{aksiPelanggan(d, online)}</div>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
        </div>
        </div>

        <div className="list-cards" style={{ padding: 14 }}>
          {loading ? (
            <EmptyState icon={Users} title="Memuat..." />
          ) : data.length === 0 ? (
            <EmptyState icon={Users} title="Belum ada kontak pelanggan" />
          ) : data.map(d => {
            const online = onlineSet ? onlineSet.has(d.ppp_username) : null
            return (
              <div className="card-row" key={d.ppp_username}>
                <div className="card-row-top">
                  <div>
                    <div className="card-row-title">{d.nama || '-'}</div>
                    <div className="card-row-sub">{d.ppp_username}</div>
                  </div>
                  <Badge tone={STATUS_TONE[d.status] || 'neutral'}>{d.status || 'AKTIF'}</Badge>
                </div>
                <div className="card-row-fields">
                  <span>{d.nama_paket || '-'}<BonusBadge list={bonusMap[d.ppp_username]} /></span>
                  <span>{d.no_hp}</span>
                  {online === null ? <span style={{ color: 'var(--text-faint)' }}>Mikrotik: -</span> :
                    <Badge tone={online ? 'success' : 'neutral'}>
                      {online ? <Wifi size={11} /> : <WifiOff size={11} />} {online ? 'Online' : 'Offline'}
                    </Badge>}
                </div>
                <div className="card-row-actions">{aksiPelanggan(d, online)}</div>
              </div>
            )
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tampilkan</span>
          <Select value={limit} onChange={e => { setPage(1); setLimit(Number(e.target.value)) }} style={{ width: 80 }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
        </div>
        <Pagination page={page} limit={limit} total={total} onChange={setPage} />
      </Card>

      {modal && (
        <Modal onClose={() => setModal(false)} width={560}>
          <ModalHeader title={editingUsername ? 'Edit Pelanggan' : 'Daftar Pelanggan Baru'} onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Username PPP *" style={{ marginBottom: 14 }}>
            <Input
              value={form.ppp_username}
              onChange={e => setForm({ ...form, ppp_username: e.target.value })}
              placeholder="mis. budi01"
              disabled={!!editingUsername}
            />
          </Field>
          <Field label="Password PPPoE" style={{ marginBottom: 14 }}>
            <Input value={form.ppp_password || ''} onChange={e => setForm({ ...form, ppp_password: e.target.value })} placeholder="Password router" />
          </Field>
          <Field label="Nomor HP *" style={{ marginBottom: 14 }}>
            <Input value={form.no_hp} onChange={e => setForm({ ...form, no_hp: e.target.value })} placeholder="08xx / 62xx / +62xx" />
          </Field>
          <Field label="Nama" style={{ marginBottom: 14 }}>
            <Input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} placeholder="Nama pelanggan" />
          </Field>

          {/* Field billing (2026-07-22, diperluas ke mode Tambah jg saat itu juga, TERMASUK utk
              teknisi 2026-07-22 kedua -- lihat perluasan akses PSB, admin tidak perlu lagi ikut
              campur lengkapi belakangan). Kalau Prabayar dipilih SAAT Tambah, tagihan prorata
              langsung dibuat pas klik Daftar (lihat preview di bawah & buatTagihanProrataAwal
              di server) -- kalau Pascabayar, tidak ada tagihan dibuat sama sekali di titik ini,
              nunggu siklus normal "Generate Tagihan Bulan Ini". */}
          <>
              <Field label="Alamat" style={{ marginBottom: 14 }}>
                <Textarea rows={2} value={form.alamat || ''} onChange={e => setForm({ ...form, alamat: e.target.value })} placeholder="Alamat pelanggan" />
              </Field>
              <Field label={editingUsername ? 'NIK' : 'NIK *'} style={{ marginBottom: 14 }}>
                <Input value={form.nik || ''} onChange={e => setForm({ ...form, nik: e.target.value })} placeholder="16 digit NIK KTP" />
              </Field>
              <Field label="NPWP (opsional)" style={{ marginBottom: 14 }}>
                <Input value={form.npwp || ''} onChange={e => setForm({ ...form, npwp: e.target.value })} placeholder="Boleh dikosongkan" />
              </Field>
              <Field label="Paket Langganan" style={{ marginBottom: 14 }}>
                <Select value={form.paket_langganan_id || ''} onChange={e => setForm({ ...form, paket_langganan_id: e.target.value || null })}>
                  <option value="">- Belum dipilih -</option>
                  {paketList.map(p => <option key={p.id} value={p.id}>{p.nama} (Rp {Number(p.harga_bulanan).toLocaleString('id-ID')})</option>)}
                </Select>
              </Field>
              <Field label="Lokasi Pelanggan" style={{ marginBottom: 14 }}>
                <div className="grid-2" style={{ gap: 10, marginBottom: 8 }}>
                  <Input value={form.latitude || ''} onChange={e => setForm({ ...form, latitude: e.target.value })} placeholder="Latitude" />
                  <Input value={form.longitude || ''} onChange={e => setForm({ ...form, longitude: e.target.value })} placeholder="Longitude" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <Button type="button" size="sm" variant="soft" icon={MapPin} loading={lokasiLoading} onClick={ambilLokasi}>Ambil Lokasi Saat Ini</Button>
                  {form.latitude && form.longitude && (
                    <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>Buka di Peta</a>
                  )}
                </div>
                {lokasiError && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>{lokasiError}</div>}
                {/* Peta geser (2026-07-22) -- pin diam di tengah kontainer, map di baliknya yg
                    digeser, spy teknisi bisa fine-tune titik lokasi persis di depan rumah pelanggan.
                    Muncul begitu ada koordinat awal (dari Ambil Lokasi atau diketik manual). */}
                {form.latitude && form.longitude && (
                  <div style={{ marginTop: 10 }}>
                    <LokasiMap
                      ref={lokasiMapRef}
                      lat={form.latitude}
                      lng={form.longitude}
                      onChange={(lat, lng) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>Geser peta sampai pin merah tepat di lokasi pelanggan.</div>
                  </div>
                )}
              </Field>
              <Field label="IP Address" style={{ marginBottom: 14 }}>
                <Input value={form.ip_address || ''} onChange={e => setForm({ ...form, ip_address: e.target.value })} placeholder="Kalau IP statis" />
              </Field>
              <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
                <Field label="Tanggal Aktif">
                  <Input type="date" value={form.tanggal_aktif || ''} onChange={e => setForm({ ...form, tanggal_aktif: e.target.value })} />
                </Field>
                <Field label="Tgl Jatuh Tempo (1-28)">
                  <Input type="number" min={1} max={28} value={form.tanggal_jatuh_tempo || 1} onChange={e => setForm({ ...form, tanggal_jatuh_tempo: parseInt(e.target.value) || 1 })} />
                </Field>
              </div>
              <div className="grid-2" style={{ gap: 10, marginBottom: editingUsername ? 22 : 14 }}>
                <Field label="Metode Bayar">
                  <Select value={form.metode_bayar || 'PASCABAYAR'} onChange={e => setForm({ ...form, metode_bayar: e.target.value })}>
                    <option value="PASCABAYAR">Pascabayar</option>
                    <option value="PRABAYAR">Prabayar</option>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status || 'AKTIF'} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="AKTIF">Aktif</option>
                    <option value="ISOLIR">Isolir</option>
                    <option value="BERHENTI">Berhenti</option>
                  </Select>
                </Field>
              </div>

              {/* Preview tagihan prorata -- cuma di mode Tambah (registrasi baru), lihat catatan di atas */}
              {!editingUsername && form.metode_bayar === 'PRABAYAR' && (() => {
                const paket = paketList.find(p => String(p.id) === String(form.paket_langganan_id))
                if (!paket) {
                  return (
                    <Alert type="error">Pilih Paket Langganan dulu utk menghitung tagihan prorata.</Alert>
                  )
                }
                const pr = hitungProrata(paket.harga_bulanan, form.tanggal_aktif)
                return (
                  <div style={{
                    background: 'var(--brand-soft)', color: 'var(--brand-soft-text)', borderRadius: 'var(--radius-sm)',
                    padding: '11px 14px', fontSize: 12.5, marginBottom: 22, lineHeight: 1.5
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>Tagihan Prorata: Rp {Number(pr.jumlah).toLocaleString('id-ID')}</div>
                    <div>{pr.sisaHari} hari ({pr.tglAktif} s/d akhir bulan) dari paket Rp {Number(paket.harga_bulanan).toLocaleString('id-ID')}/bulan.</div>
                    <div style={{ marginTop: 2 }}>Langsung tercatat di menu Tagihan begitu klik Daftar.</div>
                  </div>
                )
              })()}
              {!editingUsername && form.metode_bayar !== 'PRABAYAR' && (
                <div style={{
                  background: '#f2f2f5', color: 'var(--text-muted)', borderRadius: 'var(--radius-sm)',
                  padding: '11px 14px', fontSize: 12.5, marginBottom: 22, lineHeight: 1.5
                }}>
                  Pascabayar: tidak ada tagihan dibuat sekarang. Pelanggan ini masuk ke tagihan lewat "Generate Tagihan Bulan Ini" seperti biasa.
                </div>
              )}
            </>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>{editingUsername ? 'Simpan' : 'Daftar'}</Button>
          </div>
        </Modal>
      )}

      {detailModal && (
        <Modal onClose={() => setDetailModal(false)} width={420}>
          <ModalHeader title="Detail Pelanggan" onClose={() => setDetailModal(false)} />
          {detailLoading || !detailData ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Memuat...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
              <DetailRow label="Username PPP" value={detailData.ppp_username} />
              <DetailRow label="Nama" value={detailData.nama} />
              <DetailRow label="Nomor HP" value={detailData.no_hp} />
              <DetailRow label="Alamat" value={detailData.alamat} />
              <DetailRow label="NPWP" value={detailData.npwp} />
              <DetailRow label="Lokasi" value={
                detailData.latitude && detailData.longitude
                  ? <a href={`https://www.google.com/maps?q=${detailData.latitude},${detailData.longitude}`} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>{detailData.latitude}, {detailData.longitude} — Buka di Peta</a>
                  : '-'
              } raw />
              <DetailRow label="Paket" value={detailData.nama_paket} />
              <DetailRow label="IP Address" value={detailData.ip_address} />
              <DetailRow label="Tanggal Aktif" value={detailData.tanggal_aktif} />
              <DetailRow label="Metode Bayar" value={detailData.metode_bayar === 'PRABAYAR' ? 'Prabayar' : 'Pascabayar'} />
              <DetailRow label="Tgl Jatuh Tempo" value={detailData.tanggal_jatuh_tempo ? `Tanggal ${detailData.tanggal_jatuh_tempo}` : '-'} />
              <DetailRow label="Status" value={
                <Badge tone={STATUS_TONE[detailData.status] || 'neutral'}>{detailData.status || 'AKTIF'}</Badge>
              } raw />
              {detailData.status_diubah_at && <DetailRow label="Status Sejak" value={detailData.status_diubah_at} />}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>NIK</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace' }}>
                    {detailData.nik ? (showNik ? detailData.nik : maskNik(detailData.nik)) : '-'}
                  </span>
                  {detailData.nik && (
                    <button onClick={() => setShowNik(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                      {showNik ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Password PPPoE</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace' }}>
                    {detailData.ppp_password ? (showPassword ? detailData.ppp_password : '••••••••') : '-'}
                  </span>
                  {detailData.ppp_password && (
                    <button onClick={() => setShowPassword(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  )}
                </div>
              </div>

              {/* Link Tagihan Publik (2026-07-22) -- fase 1 notifikasi isolir, dibagikan MANUAL ke
                  pelanggan yg nunggak (WA pribadi/dicetak), lihat catatan status ISOLIR di server. */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Link Tagihan Publik</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{linkTagihan}</span>
                  <Button size="sm" variant="soft" onClick={() => navigator.clipboard?.writeText(linkTagihan)} style={{ flexShrink: 0 }}>Salin</Button>
                </div>
              </div>

              {/* Dompet/Deposit Pelanggan -- saldo dari kelebihan bayar/bayar di muka, otomatis
                  dipakai saat Generate Tagihan bulan berikutnya. Tombol Deposit buka modal cepat
                  yg sama dgn ikon Wallet di baris tabel (admin+teknisi, lihat bukaDeposit). */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Saldo Dompet</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: saldoDompet > 0 ? 'var(--success)' : 'var(--text)' }}>
                    Rp {Number(saldoDompet || 0).toLocaleString('id-ID')}
                  </div>
                  <Button size="sm" variant="soft" icon={Wallet} onClick={() => bukaDeposit(detailData)}>Deposit</Button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {diagUsername && <DiagnostikModal username={diagUsername} onClose={() => setDiagUsername(null)} />}

      {depositUser && (
        <Modal onClose={() => setDepositUser(null)} width={360}>
          <ModalHeader title="Deposit" subtitle={depositUser.nama || depositUser.ppp_username} onClose={() => setDepositUser(null)} />
          {depositError && <Alert type="error">{depositError}</Alert>}
          <Field label="Jumlah (Rp) *" style={{ marginBottom: 22 }}>
            <Input type="number" autoFocus value={depositJumlah} onChange={e => setDepositJumlah(e.target.value)} placeholder="0"
              onKeyDown={e => e.key === 'Enter' && simpanDeposit()} />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setDepositUser(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button icon={Wallet} onClick={simpanDeposit} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {/* Toast Deposit (2026-07-28) -- lihat komentar showToast()/simpanDeposit() */}
      {toast && (
        <div style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 1000, maxWidth: 340,
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md, 0 6px 20px rgba(0,0,0,0.15))',
          background: 'var(--surface)', border: '1px solid var(--border)', fontSize: 13
        }}>
          {toast.status === 'loading' && <Loader2 size={17} className="spin" style={{ flexShrink: 0, marginTop: 1, color: 'var(--text-muted)' }} />}
          {toast.status === 'success' && <CheckCircle2 size={17} style={{ flexShrink: 0, marginTop: 1, color: 'var(--success)' }} />}
          {toast.status === 'error' && <XCircle size={17} style={{ flexShrink: 0, marginTop: 1, color: 'var(--danger)' }} />}
          <div style={{ lineHeight: 1.4 }}>{toast.text}</div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value, raw }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>{label}</div>
      <div>{raw ? value : (value || '-')}</div>
    </div>
  )
}
